import { IngressRequestForm } from '@dispatch/shared/types';
import { Section } from '@dispatch/web/ui';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { UseFormGetValues } from 'react-hook-form';
import { startCase } from 'lodash';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { createDateWithTimeZoneOffset } from '@dispatch/shared';

export type IngressRequestReviewSectionProps = {
  getValues: UseFormGetValues<IngressRequestForm>;
  timeZone: string;
};

export const IngressRequestReviewSection = ({
  getValues,
  timeZone = '',
}: IngressRequestReviewSectionProps) => {
  const startCaseProperties = ['taskType', 'assignmentType', 'inspectionType'];

  const renderHelper = (object: unknown, title?: string) => {
    const renderObject = (object: unknown) => {
      return Object.entries(object as object).map(([key, value]) => {
        if (typeof value === 'object') {
          return renderHelper(value, key);
        }

        if (typeof value === 'boolean') {
          return (
            <Box display={'flex'} alignItems={'baseline'}>
              <Typography fontWeight={600}>{startCase(key)}: </Typography>
              <Typography>{value ? 'Yes' : 'No'}</Typography>
            </Box>
          );
        }

        if (!value) {
          return null;
        }

        return (
          <Box display={'flex'} alignItems={'baseline'}>
            <Typography fontWeight={600}>{startCase(key)}: </Typography>
            &nbsp;
            <Typography>
              {startCaseProperties.includes(key) ? startCase(value) : value}
            </Typography>
          </Box>
        );
      });
    };

    return <Section title={title}>{renderObject(object)}</Section>;
  };

  const renderClaimAttributes = () => {
    return getValues('claimAttributes').map((attribute) => {
      return (
        <Stack direction={'row'} display={'flex'} alignItems={'baseline'}>
          <Typography fontWeight={600} noWrap>
            {attribute.displayName}:{' '}
          </Typography>
          &nbsp;
          {attribute.selectedAttributeValuesVarchars.map((value, index) => {
            const item = index > 0 ? `, ${value}` : value;
            return <Typography>{item}</Typography>;
          })}
        </Stack>
      );
    });
  };

  const renderDateTime = (name: string, dateTime?: string) => {
    if (!dateTime) {
      return null;
    }

    return (
      <Box display={'flex'} alignItems={'baseline'}>
        <Typography fontWeight={600}>{name}: </Typography>
        &nbsp;
        <Typography>
          {format(new Date(dateTime), 'eeee MM/dd/yyyy hh:mm aa')}
        </Typography>
      </Box>
    );
  };

  const {
    lossLocation: _,
    dateOfLoss: __,
    dateOfNotice: ___,
    ...claimWithoutLossLocation
  } = getValues('claim');
  const {
    mustEndBefore: ____,
    mustStartAfter: _______,
    ...assignmentHandlingWithoutDates
  } = getValues('assignmentHandling');

  return (
    <Section title="Review">
      <Grid container spacing={2}>
        <Grid size={6}>
          {renderHelper(getValues('claim.lossLocation'), 'Loss Location')}
        </Grid>
        {getValues('contact.hasAlternateLocation').valueOf() && (
          <Grid size={6}>
            {renderHelper(
              getValues('contact.altLocation'),
              'Alternate Location'
            )}
          </Grid>
        )}
        <Divider style={{ marginTop: 4, marginBottom: 20, marginLeft: -16 }} />
        <Grid size={12}>
          {renderHelper(claimWithoutLossLocation, 'Claim Information')}
          {renderDateTime('Date of Loss', getValues('claim.dateOfLoss'))}
          {renderDateTime('Date of Notice', getValues('claim.dateOfNotice'))}
        </Grid>
        <Divider style={{ marginTop: 4, marginBottom: 20, marginLeft: -16 }} />
        <Grid size={6}>
          {renderHelper(getValues('contact.primary'), 'Primary Contact')}
        </Grid>
        {getValues('contact.hasSecondaryContact').valueOf() && (
          <Grid size={6}>
            {renderHelper(getValues('contact.secondary'), 'Secondary Contact')}
          </Grid>
        )}
        <Grid size={12}>
          {renderHelper(assignmentHandlingWithoutDates, 'Assignment Handling')}
          {renderDateTime(
            'Must Start After',
            getValues('assignmentHandling.mustStartAfter')
          )}
          {renderDateTime(
            'Must End Before',
            getValues('assignmentHandling.mustEndBefore')
          )}
        </Grid>
        <Grid container spacing={0} size={12}>
          <Section title="Other Information">
            <Grid size={12}>
              <Box display={'flex'} alignItems={'baseline'}>
                <Typography fontWeight={600}>Required Resource: </Typography>
                &nbsp;
                <Typography>{getValues('requiredResource.title')}</Typography>
              </Box>
            </Grid>
            <Grid size={12}>
              <Box display={'flex'} alignItems={'baseline'}>
                <Typography fontWeight={600}>Task Type: </Typography>
                &nbsp;
                <Typography>
                  {startCase(getValues('assignmentHandling.taskType'))}
                </Typography>
              </Box>
            </Grid>
          </Section>
        </Grid>
        {getValues('claimAttributes').length > 0 && (
          <Grid size={12}>
            <Section title="Claim Attributes">
              {renderClaimAttributes()}
            </Section>
          </Grid>
        )}
      </Grid>
    </Section>
  );
};
